import { NavLink } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface NavItemConfig {
  icon: LucideIcon
  label: string
  path: string
  end?: boolean
  children?: NavItemConfig[]
  badge?: React.ReactNode
  /**
   * Gating §32 (M1.b "luz vs marcas"): si está presente, este ítem es del
   * GRUPO 2 ("las marcas" — capacidad que el cliente OFRECE/usa) y solo se
   * muestra si el CO activo tiene suscripción §32 a esta entidad. Sin
   * `gateEntidad` = GRUPO 1 ("la luz" — conector-universal/BaaS, siempre visible).
   * Nombre canónico de la entidad requerida, ej: 'AARPIA.infra.entidad.disponible'.
   */
  gateEntidad?: string
}

interface Props {
  item: NavItemConfig
  collapsed: boolean
  /** Override del comportamiento NavLink (p.ej. botón con onClick custom) */
  onClick?: () => void
}

/**
 * Item de navegación en la sidebar. Activo se marca con barra derecha de
 * 2px en color primary + bg-primary/10 + text-primary. Patrón machette-tech
 * (`packages/app-shell/SidebarNavItem.tsx`).
 */
export function SidebarNavItem({ item, collapsed, onClick }: Props) {
  const Icon = item.icon

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={collapsed ? item.label : undefined}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
          'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground',
          collapsed && 'justify-center px-0',
        )}
      >
        <Icon className="h-5 w-5 shrink-0" />
        {!collapsed && <span className="truncate">{item.label}</span>}
        {!collapsed && item.badge}
      </button>
    )
  }

  return (
    <NavLink
      to={item.path}
      end={item.end}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
          isActive
            // Fix contraste: el activo usa las variables del SIDEBAR (no --primary, que
            // el branding puede poner casi-negro → text-primary invisible sobre el sidebar
            // oscuro, contraste 1.07:1). sidebar-foreground/accent no los toca la marca →
            // texto siempre legible; el acento de marca va en la barra lateral (bg-accent).
            ? 'bg-sidebar-accent text-sidebar-foreground font-semibold'
            : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground',
          collapsed && 'justify-center px-0',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute inset-y-1 right-0 w-0.5 rounded-l-full bg-accent" aria-hidden />
          )}
          <Icon className="h-5 w-5 shrink-0" />
          {!collapsed && <span className="truncate">{item.label}</span>}
          {!collapsed && item.badge}
        </>
      )}
    </NavLink>
  )
}
